import React, { useEffect, useState } from 'react';
import { cn } from '../../../../components/ui/_cn';
import FinDropdown from '../../../connect/finance/fin-dropdown';
import { productHasOptions } from '../../lib/campaign-rule';

// The rule as one sentence (handoff §2 step 2, §4; §7 "Rule"). `hero` is
// step 2 of the New campaign modal: 20px/700 words with 40px blue chips
// (#EEF2F9 fin-selected, #1D4ED8 fin-blue-deep, 19px/700). `compact` is the
// Edit modal's left column: the form's 13.5px words and bordered fields.
// The control reports patches of the rule shape (lib/campaign-rule.js); the
// modal owns the rule and its checks.
export const RULE_COPY = {
  product: 'Product',
  option: 'Option',
  loading: 'Loading…',
  loadFailed: 'Couldn’t load products.',
  retry: 'Retry',
  noProducts: 'No products to choose from yet.',
};

// Typing keeps digits and one dot with two decimals; a quantity keeps digits.
const clean = (raw, integer) => {
  if (integer) return raw.replace(/\D/g, '').slice(0, 4);
  const [whole, ...rest] = raw.replace(/[^\d.]/g, '').split('.');
  return rest.length ? `${whole}.${rest.join('').slice(0, 2)}` : whole;
};
const parse = (text, integer) => {
  if (text === '' || text === '.') return null;
  return integer ? parseInt(text, 10) : Number(text);
};
const shown = (value, money) => {
  if (typeof value !== 'number') return '';
  return money ? value.toFixed(2) : String(value);
};

const NumberField = ({ label, value, onChange, money = false, integer = false, hero, disabled, widthClass, children }) => {
  const [text, setText] = useState(() => shown(value, money));
  // Follow a value set from outside (a reset, a type switch) but keep the
  // reader's own spelling ("12." mid-typing) while it means the same number.
  useEffect(() => {
    setText((current) => (parse(current, integer) === (value ?? null) ? current : shown(value, money)));
  }, [value, money, integer]);
  return (
    <span
      className={cn(
        'inline-flex items-center',
        hero
          ? 'h-10 rounded-lg bg-fin-selected px-3 text-[19px] font-bold text-fin-blue-deep'
          : 'h-9 rounded-lg border border-fin-border-2 bg-fin-surface px-3 text-[13.5px] text-fin-ink',
        children && 'pr-1',
        disabled && 'opacity-60',
      )}
    >
      {money ? (
        <span aria-hidden="true" className={hero ? 'mr-0.5' : 'mr-1 text-fin-faint'}>
          $
        </span>
      ) : null}
      <input
        type="text"
        inputMode={integer ? 'numeric' : 'decimal'}
        aria-label={label}
        value={text}
        disabled={disabled}
        placeholder={money ? '0.00' : '0'}
        onChange={(event) => {
          const next = clean(event.target.value, integer);
          setText(next);
          onChange(parse(next, integer));
        }}
        onBlur={() => {
          if (money && typeof value === 'number') setText(value.toFixed(2));
        }}
        className={cn('min-w-0 bg-transparent outline-none placeholder:text-fin-faint', widthClass)}
      />
      {children}
    </span>
  );
};

// The `% | $` segment inside the discount chip; the chosen one is white on
// blue (handoff §2).
const UnitToggle = ({ unit, onChange, hero, disabled }) => (
  <span role="group" aria-label="Discount unit" className={cn('ml-2 inline-flex rounded-md p-0.5', hero ? 'bg-fin-surface' : 'bg-fin-hair')}>
    {[
      ['percent', '%', 'Percent'],
      ['fixed', '$', 'Dollars'],
    ].map(([key, glyph, name]) => (
      <button
        key={key}
        type="button"
        aria-label={name}
        aria-pressed={unit === key}
        disabled={disabled}
        onClick={() => onChange(key)}
        className={cn(
          'inline-flex h-7 min-w-[28px] items-center justify-center rounded px-1.5 text-[13px] font-bold',
          unit === key ? 'bg-fin-blue text-white' : 'text-fin-muted hover:text-fin-ink',
        )}
      >
        {glyph}
      </button>
    ))}
  </span>
);

const RuleSentence = ({
  rule,
  onChange,
  products = [],
  productsState = 'ready',
  onRetryProducts,
  errors = {},
  variant = 'hero',
  disabled = false,
  fallback = {},
}) => {
  if (!rule) return null;
  const hero = variant === 'hero';
  const product = products.find((choice) => choice.productid === rule.productId) || null;
  const loadingProducts = productsState === 'loading';

  // One item means no option to choose: that item is the free item.
  const pickProduct = (productid) => {
    const next = products.find((choice) => choice.productid === productid);
    if (rule.type === 'free_item') {
      onChange({ productId: productid, itemId: next && next.items.length === 1 ? next.items[0].id : null });
    } else {
      onChange({ productId: productid });
    }
  };

  const productSelect = (joined) => (
    <FinDropdown
      mode="radio"
      size="md"
      chip={hero}
      bare={hero && joined}
      aria-label={RULE_COPY.product}
      value={rule.productId}
      label={!product && (rule.productId || rule.itemId) && fallback.product ? fallback.product : undefined}
      options={products.map((choice) => ({ value: choice.productid, label: choice.name }))}
      placeholder={loadingProducts ? RULE_COPY.loading : RULE_COPY.product}
      disabled={disabled || loadingProducts || products.length === 0}
      onChange={pickProduct}
    />
  );

  // Handoff §4: the option select waits for the product, and is not drawn
  // for a product without a main option.
  const showOption = rule.type === 'free_item' && (!product || productHasOptions(product));
  const optionSelect = (
    <FinDropdown
      mode="radio"
      size="md"
      chip={hero}
      bare={hero}
      aria-label={RULE_COPY.option}
      value={rule.itemId}
      label={!product && rule.itemId && fallback.item ? fallback.item : undefined}
      options={(product?.items || []).map((item) => ({ value: item.id, label: item.name }))}
      placeholder={RULE_COPY.option}
      disabled={disabled || !product}
      onChange={(itemId) => onChange({ itemId })}
    />
  );

  let freeItem = productSelect(false);
  if (showOption && hero) {
    freeItem = (
      <span className="inline-flex items-center rounded-lg bg-fin-selected">
        {productSelect(true)}
        <span aria-hidden="true" className="h-6 w-px bg-fin-border-2" />
        {optionSelect}
      </span>
    );
  } else if (showOption) {
    freeItem = (
      <span className="inline-flex flex-wrap items-center gap-2">
        {productSelect(false)}
        {optionSelect}
      </span>
    );
  }

  const minSpend = (
    <NumberField
      label="Minimum spend"
      money
      value={rule.minSpend}
      onChange={(next) => onChange({ minSpend: next })}
      hero={hero}
      disabled={disabled}
      widthClass={hero ? 'w-[6ch]' : 'w-20'}
    />
  );

  let words;
  if (rule.type === 'discount') {
    words = (
      <>
        <span>Spend at least</span>
        {minSpend}
        <span>and get</span>
        <NumberField
          label="Discount"
          value={rule.amount}
          onChange={(next) => onChange({ amount: next })}
          hero={hero}
          disabled={disabled}
          widthClass={hero ? 'w-[4ch]' : 'w-14'}
        >
          <UnitToggle unit={rule.unit} hero={hero} disabled={disabled} onChange={(unit) => onChange({ unit })} />
        </NumberField>
        <span>off.</span>
      </>
    );
  } else if (rule.type === 'free_item') {
    words = (
      <>
        <span>Spend at least</span>
        {minSpend}
        <span>and get a free</span>
        {freeItem}
      </>
    );
  } else {
    words = (
      <>
        <span>Buy</span>
        <NumberField
          label="Buy quantity"
          integer
          value={rule.buyQty}
          onChange={(next) => onChange({ buyQty: next })}
          hero={hero}
          disabled={disabled}
          widthClass={hero ? 'w-[2.5ch]' : 'w-10'}
        />
        {productSelect(false)}
        <span>and get</span>
        <NumberField
          label="Free quantity"
          integer
          value={rule.getQty}
          onChange={(next) => onChange({ getQty: next })}
          hero={hero}
          disabled={disabled}
          widthClass={hero ? 'w-[2.5ch]' : 'w-10'}
        />
        <span>free.</span>
      </>
    );
  }

  const messages = [...new Set(Object.values(errors).filter(Boolean))];
  const needsProducts = rule.type !== 'discount';

  return (
    <div className="w-full">
      <div
        className={cn(
          'flex flex-wrap items-center',
          hero ? 'gap-x-2.5 gap-y-3 text-[20px] font-bold leading-10 text-fin-ink' : 'gap-2 text-[13.5px] text-fin-ink-2',
        )}
      >
        {words}
      </div>
      {needsProducts && productsState === 'failed' ? (
        <p className="mt-3 text-[13px] text-fin-red">
          {RULE_COPY.loadFailed}{' '}
          <button type="button" onClick={onRetryProducts} className="font-semibold underline">
            {RULE_COPY.retry}
          </button>
        </p>
      ) : null}
      {needsProducts && productsState === 'ready' && products.length === 0 ? (
        <p className="mt-3 text-[13px] text-fin-muted">{RULE_COPY.noProducts}</p>
      ) : null}
      {messages.length ? (
        <div className="mt-3 space-y-1">
          {messages.map((message) => (
            <p key={message} className="text-[12.5px] text-fin-red">
              {message}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default RuleSentence;
